// ============================================================================
// Tour de BIENVENIDA AL PANEL del manager (dueño/operador de negocio) — enseña
// dónde vive todo en 1 minuto. Consumidor del módulo cross src/lib/tour/
// (coach-marks visto-una-vez, persistido en users.tours de lealify-base).
//
// Cuándo dispara (una sola vez por operador, cruza dispositivos):
//  - negocio NUEVO: al caer al dashboard tras completar el wizard de onboarding
//    (needsOnboarding=false) — complementa al wizard (config) y al checklist de
//    activación (primeros 10 clientes), NO los repite.
//  - negocio EXISTENTE: en su siguiente visita al dashboard. Los pasos cuyo
//    target no exista (p.ej. el checklist, que desaparece a los 10 clientes) se
//    SALTAN solos — el motor nunca apunta a nada invisible.
// Relanzable con el botón de ayuda del header (evento lealify:start-tour).
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tour from '../lib/tour/Tour';
import { TourStep } from '../lib/tour/types';
import { useAuth } from '../context/AuthContext';

export const MANAGER_TOUR_KEY = 'manager_panel_v1';
const KIOSK_URL = 'https://app.lealify.com';

// En móvil el menú lateral es un drawer cerrado: los pasos que apuntan a ítems
// del nav lo abren antes de medir (Layout escucha estos eventos; en desktop el
// sidebar es fijo y son no-op visual).
const openSidebar = () => window.dispatchEvent(new CustomEvent('lealify:open-sidebar'));
const closeSidebar = () => window.dispatchEvent(new CustomEvent('lealify:close-sidebar'));

// Guard a nivel módulo: el contexto no refresca `tours` tras marcar visto, y este
// componente vive dentro de Layout (se remonta al navegar) — sin esto, volver a
// /dashboard en la misma sesión re-dispararía el auto-arranque.
let autoFired = false;

const ManagerTour: React.FC = () => {
  const { currentUser, needsOnboarding, tours } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Solo operadores de negocio (el super_admin de plataforma no tiene panel de dueño).
  const isOperator = !!currentUser && currentUser.role !== 'admin';

  // Auto-arranque: una vez, en el dashboard, con el wizard ya completado.
  useEffect(() => {
    if (autoFired || !isOperator || needsOnboarding || pathname !== '/dashboard') return;
    if (!tours || tours[MANAGER_TOUR_KEY]) return;
    autoFired = true;
    // Pequeña espera: los paneles del dashboard (checklist incluido) cargan async.
    const t = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(t);
  }, [isOperator, needsOnboarding, tours, pathname]);

  // Relanzo manual (botón de ayuda del header). Si no estamos en el dashboard,
  // navegamos primero para que existan los targets del inicio.
  useEffect(() => {
    const start = () => {
      if (window.location.pathname !== '/dashboard') {
        navigate('/dashboard');
        window.setTimeout(() => setOpen(true), 450);
      } else {
        setOpen(true);
      }
    };
    window.addEventListener('lealify:start-tour', start);
    return () => window.removeEventListener('lealify:start-tour', start);
  }, [navigate]);

  const steps: TourStep[] = useMemo(() => [
    {
      emoji: '👋',
      title: '¿Nos das un minuto?',
      body: 'Te enseñamos dónde vive todo en tu panel Lealify. Puedes saltarlo cuando quieras y repetirlo después desde el botón de ayuda (?) de arriba.',
    },
    {
      // Solo negocios en arranque: el checklist desaparece a los 10 clientes
      // (o si el dueño lo ocultó) → el motor salta este paso solo.
      target: '[data-tour="activation-checklist"]',
      emoji: '🚀',
      title: 'Tu guía de arranque',
      body: 'Estos pasos ponen a andar tu programa de lealtad. Al llegar a 10 clientes registrados, esta guía desaparece sola: tu programa ya camina.',
    },
    {
      target: '[data-tour="nav-register-client"]',
      onEnter: openSidebar,
      enterDelayMs: 420,
      emoji: '👤',
      title: 'Registra a cada cliente que pague',
      body: 'Aquí das de alta clientes a mano. El hábito que hace crecer todo: cada venta, un registro — aunque lo normal es que se registren solos en el kiosco (te lo enseñamos al final).',
    },
    {
      target: '[data-tour="nav-rewards"]',
      onEnter: openSidebar,
      enterDelayMs: 120,
      emoji: '🎁',
      title: 'Tus recompensas',
      body: 'Crea y edita los premios que tus clientes van juntando. Cuando alguien alcance su meta, lo canjeas en "Canjear Recompensas", en el grupo VISITAS del menú.',
    },
    {
      target: '[data-tour="nav-campaigns"]',
      onEnter: openSidebar,
      enterDelayMs: 120,
      onLeave: closeSidebar,
      emoji: '📣',
      title: 'Trae de vuelta a los dormidos',
      body: 'Con Campañas mandas WhatsApp a tu lista: promos, avisos, reactivación de clientes inactivos. Es de las funciones que más visitas recuperan (plan PRO).',
    },
    {
      emoji: '📱',
      title: 'El kiosco: lo que vive tu cliente',
      body: 'En app.lealify.com tus clientes teclean su número y suman puntos solos (entra con estas mismas credenciales). El mejor primer paso: pruébalo TÚ con tu propio celular y vívelo como ellos.',
      actionLabel: '📱 Abrir el kiosco',
      onAction: () => window.open(KIOSK_URL, '_blank', 'noopener,noreferrer'),
    },
  ], []);

  if (!isOperator) return null;
  return (
    <Tour
      tourKey={MANAGER_TOUR_KEY}
      steps={steps}
      open={open}
      onClose={() => { closeSidebar(); setOpen(false); }}
      farewell={{
        emoji: '🎉',
        title: '¡Listo para crecer!',
        body: 'Eso es todo. Cualquier duda, el botón de ayuda (?) de arriba repite este recorrido, y en Preguntas Frecuentes está el resto.',
        ctaLabel: '¡A registrar clientes!',
      }}
    />
  );
};

export default ManagerTour;
